import React, { useState, useEffect } from 'react';
import { Printer, Check } from 'lucide-react';

const PrintingReceipt = ({ onComplete, amount, config }) => {
  const [printingStage, setPrintingStage] = useState('printing'); // 'printing' or 'complete'
  const [printedLines, setPrintedLines] = useState(0);

  const receiptLines = [
    'RECEIPT',
    '========================',
    `DATE: ${new Date().toLocaleDateString()}`,
    `TIME: ${new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`,
    'CARD: ****1234',
    'TYPE: SALE',
    `AMOUNT: $${amount || '0.00'}`,
    'APPROVAL: 123456',
    `REF: ${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
    '========================',
    'THANK YOU FOR YOUR BUSINESS'
  ];

  useEffect(() => {
    if (printingStage === 'printing') {
      const interval = setInterval(() => {
        setPrintedLines(prev => {
          const nextLine = prev + 1;
          if (nextLine >= receiptLines.length) {
            setTimeout(() => setPrintingStage('complete'), 500);
            clearInterval(interval);
            return receiptLines.length;
          }
          return nextLine;
        });
      }, 300); // Print one line every 300ms

      return () => clearInterval(interval);
    }
  }, [printingStage, receiptLines.length]);

  useEffect(() => {
    if (printingStage === 'complete') {
      const timer = setTimeout(() => {
        onComplete();
      }, 2000); // Show complete state for 2 seconds
      
      return () => clearTimeout(timer);
    }
  }, [printingStage, onComplete]);

  return (
    <div className={`bg-white text-gray-800 h-full flex flex-col items-center justify-center p-3 ${config.ui.maxWidth} mx-auto`}>
      {printingStage === 'printing' ? (
        <>
          {/* Printing Animation */}
          <div className="relative mb-6">
            <Printer className="w-16 h-16 text-blue-600 animate-bounce" />
            <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-blue-100 rounded-full animate-ping"></div>
          </div>
          <div className="text-lg font-bold text-gray-800 mb-4">Printing Receipt...</div>
          
          {/* Animated Receipt */}
          <div className="bg-white border-2 border-dashed border-gray-400 p-4 rounded-lg w-full max-w-sm shadow-lg">
            <div className="font-mono text-xs text-center space-y-1">
              {receiptLines.slice(0, printedLines).map((line, index) => (
                <div 
                  key={index} 
                  className={`transition-opacity duration-300 ${
                    index === printedLines - 1 ? 'animate-pulse' : ''
                  }`}
                >
                  {line}
                </div>
              ))}
              {printedLines < receiptLines.length && (
                <div className="h-4 flex items-center justify-center">
                  <div className="w-2 h-2 bg-blue-600 animate-pulse"></div>
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-4 text-sm text-gray-500">
            Please wait while your receipt is printing...
          </div>
        </>
      ) : (
        <>
          {/* Printing Complete */}
          <div className="relative mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="w-10 h-10 text-green-600" />
            </div>
          </div>
          <div className="text-lg font-bold text-green-600 mb-4">Receipt Printed Successfully!</div>
          <div className="text-sm text-gray-500 text-center">
            Your receipt has been printed. Please take your receipt and card.
          </div>
        </>
      )}
    </div>
  );
};

export default PrintingReceipt;